import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Mail, MessageCircle, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { clinicDateKey, dateFromKey, firstName } from '../utils/clinicTime';

interface Rental { id: number; start: string; end: string; professionalId: number; professionalName: string; professionalEmail: string | null; professionalPhone: string | null; specialty: string | null; activeAppointments: number; mine?: boolean }
interface RoomDay { roomId: number; roomName: string; open: Array<{ start: string; end: string }>; rentals: Rental[]; free: Array<{ start: string; end: string }> }
interface Day { date: string; rooms: RoomDay[]; openHours: number; rentedHours: number }

const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const initials = (name: string) => name.replace(/^(Dr|Dra|Lic)\.?\s+/i, '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

/** Which rooms are rented by whom and what is still free, day by day. Replaces the old, much larger availability view. */
const hoursOf = (r: Rental) => (toMin(r.end) - toMin(r.start)) / 60;

/**
 * Month calendar of room occupancy. Admin mode: everything, with release buttons. Professional mode (read-only):
 * who holds each room each day so colleagues can coordinate, my own rentals highlighted, a ready-made message to contact
 * the holder, and no patient information.
 */
const RoomOccupancy: React.FC<{ mode?: 'admin' | 'professional' }> = ({ mode = 'admin' }) => {
  const isPro = mode === 'professional';
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [days, setDays] = useState<Map<string, Day>>(new Map());
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [selected, setSelected] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const todayKey = clinicDateKey(new Date());

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api.get(isPro ? '/postgres/professionals/rooms/occupancy' : '/admin/room-occupancy', { params: { from: format(startOfMonth(month), 'yyyy-MM-dd'), to: format(endOfMonth(month), 'yyyy-MM-dd') } });
      setDays(new Map((r.data.days as Day[]).map(d => [d.date, d])));
      setState('ok');
    } catch { setState('error'); }
  }, [month, isPro]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const keys = Array.from(days.keys()).sort();
    setSelected(prev => (prev && days.has(prev)) ? prev : keys.includes(todayKey) ? todayKey : keys.find(k => k >= todayKey) || keys[0] || null);
  }, [days, todayKey]);

  const totals = useMemo(() => {
    let open = 0, rented = 0, mine = 0;
    days.forEach(d => {
      d.rooms.forEach(r => r.rentals.forEach(x => { if (x.mine) mine += hoursOf(x); }));
      if (d.date >= todayKey) { open += d.openHours; rented += d.rentedHours; }
    });
    return { open, rented, mine, pct: open > 0 ? Math.round((rented / open) * 100) : 0 };
  }, [days, todayKey]);

  const release = async (r: Rental, roomName: string, date: string) => {
    if (!window.confirm(`¿Liberar ${roomName} el ${date.split('-').reverse().join('/')} de ${r.start} a ${r.end} (${r.professionalName})?`)) return;
    try {
      await api.delete(`/postgres/professionals/bookings/${r.id}`);
      toast.success('Consultorio liberado');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo liberar');
    }
  };

  const pick = (key: string) => {
    setSelected(key);
    if (window.matchMedia('(max-width: 1023px)').matches) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const cal = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
  const lead = (startOfMonth(month).getDay() + 6) % 7;
  const day = selected ? days.get(selected) : undefined;
  const lo = day ? Math.min(...day.rooms.flatMap(r => [...r.open.map(o => toMin(o.start)), ...r.rentals.map(x => toMin(x.start))])) : 0;
  const hi = day ? Math.max(...day.rooms.flatMap(r => [...r.open.map(o => toMin(o.end)), ...r.rentals.map(x => toMin(x.end))])) : 1;
  const pos = (a: string, b: string) => ({ left: `${((toMin(a) - lo) / (hi - lo)) * 100}%`, width: `${((toMin(b) - toMin(a)) / (hi - lo)) * 100}%` });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{isPro ? 'Calendario de consultorios' : 'Ocupación de consultorios'}</h2>
          <p className="mt-1 text-sm text-gray-500">{isPro
            ? 'Quién tiene cada consultorio cada día. Si necesitás un horario que ya está alquilado, escribile al profesional para coordinar.'
            : 'Quién alquiló cada consultorio y qué horarios quedan libres.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-outline btn-md" aria-label="Mes anterior" onClick={() => setMonth(subMonths(month, 1))}><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[9rem] text-center font-semibold">{upper(format(month, 'MMMM yyyy', { locale: es }))}</span>
          <button className="btn btn-outline btn-md" aria-label="Mes siguiente" onClick={() => setMonth(addMonths(month, 1))}><ChevronRight className="h-4 w-4" /></button>
          <button className="btn btn-secondary btn-md" onClick={() => setMonth(new Date())}>Hoy</button>
        </div>
      </div>

      {state === 'loading' && <div className="h-64 animate-pulse rounded-2xl bg-gray-100" />}
      {state === 'error' && <div className="card p-8 text-center text-gray-500">No pudimos cargar la ocupación. <button className="font-semibold underline" onClick={load}>Reintentar</button></div>}

      {state === 'ok' && (<>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl border border-gray-200 bg-white p-3"><div className="text-2xl font-bold text-primary-700">{totals.pct}%</div><div className="text-xs text-gray-600">Alquilado (desde hoy)</div></div>
          <div className="rounded-xl border border-gray-200 bg-white p-3"><div className="text-2xl font-bold text-gray-900">{isPro ? `${Math.round(totals.mine * 10) / 10} h` : `${Math.round(totals.rented)} h`}</div><div className="text-xs text-gray-600">{isPro ? 'Tus horas este mes' : 'Horas alquiladas'}</div></div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-3"><div className="text-2xl font-bold text-green-700">{Math.round(totals.open - totals.rented)} h</div><div className="text-xs text-green-700">Horas libres</div></div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
          <div className="card p-4">
            <div className="mb-2 grid grid-cols-7 text-center text-xs font-bold uppercase tracking-wide text-gray-400">{['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i}>{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
              {cal.map(d => {
                const key = format(d, 'yyyy-MM-dd');
                const info = days.get(key);
                const pct = info && info.openHours > 0 ? Math.round((info.rentedHours / info.openHours) * 100) : 0;
                const sel = key === selected;
                return (
                  <button key={key} type="button" disabled={!info} onClick={() => pick(key)} aria-pressed={sel}
                    aria-label={info ? `${format(d, "d 'de' MMMM", { locale: es })}: ${pct}% alquilado` : `${format(d, "d 'de' MMMM", { locale: es })}: cerrado`}
                    className={`flex aspect-square flex-col items-center justify-center rounded-xl border text-sm transition-colors
                      ${sel ? 'border-primary-600 bg-primary-600 text-white' : info ? 'border-gray-200 bg-white hover:border-primary-300' : 'border-transparent text-gray-300'}
                      ${key === todayKey && !sel ? 'ring-2 ring-accent-gold' : ''}`}>
                    <span className="font-semibold">{format(d, 'd')}</span>
                    {info && <span className={`text-[10px] leading-none ${sel ? 'text-white/90' : pct >= 75 ? 'text-primary-700 font-bold' : 'text-gray-500'}`}>{pct}%</span>}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-gray-500">Cada día muestra qué porcentaje de las horas abiertas está alquilado.</p>
          </div>

          <div ref={detailRef} className="min-w-0">
            {!day ? (
              <div className="card p-10 text-center text-gray-500">No hay consultorios abiertos este mes. Configurá los horarios en Sistema → Salas.</div>
            ) : (
              <div className="card p-5">
                <h3 className="text-xl font-semibold text-gray-900">{upper(format(dateFromKey(day.date), "EEEE d 'de' MMMM", { locale: es }))}</h3>
                <p className="mt-1 text-sm text-gray-500">{day.rentedHours} h alquiladas de {day.openHours} h abiertas{isPro ? ' · en dorado, tus alquileres' : ''}</p>

                <div className="mt-4 space-y-5">
                  {day.rooms.map(room => (
                    <section key={room.roomId} aria-label={room.roomName}>
                      <h4 className="font-semibold text-gray-900">{room.roomName}</h4>
                      <div className="relative mt-2 h-8 overflow-hidden rounded-lg bg-gray-100" role="img"
                        aria-label={`${room.roomName}: ${room.rentals.length} alquiler(es), ${room.free.length} tramo(s) libre(s)`}>
                        {room.open.map((o, i) => <div key={`o${i}`} className="absolute top-0 h-full bg-green-100" style={pos(o.start, o.end)} />)}
                        {room.rentals.map(r => (
                          <div key={r.id} className={`absolute top-0 flex h-full items-center justify-center overflow-hidden text-[11px] font-bold ${r.mine ? 'bg-accent-gold text-primary-950' : 'bg-primary-600 text-white'}`} style={{ ...pos(r.start, r.end), ...(r.mine ? { color: '#1e1226' } : {}) }} title={`${r.professionalName} ${r.start}–${r.end}`}>
                            {initials(r.professionalName)}
                          </div>
                        ))}
                      </div>
                      <div className="mt-1 flex justify-between text-[11px] text-gray-400"><span>{String(Math.floor(lo / 60)).padStart(2, '0')}:00</span><span>{String(Math.floor(hi / 60)).padStart(2, '0')}:00</span></div>

                      <ul className="mt-2 space-y-1.5 text-sm">
                        {room.rentals.map(r => (
                          <li key={r.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 ${r.mine ? 'border border-accent-gold bg-amber-50' : 'bg-gray-50'}`}>
                            <span><b>{r.start}–{r.end}</b> · {r.mine ? 'Vos' : r.professionalName}{r.specialty && !r.mine ? <span className="text-gray-500"> ({r.specialty})</span> : null}
                              {!isPro && r.activeAppointments > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800"><Users className="h-3 w-3" aria-hidden="true" />{r.activeAppointments} turno{r.activeAppointments !== 1 ? 's' : ''}</span>}
                              {isPro && r.mine && <span className="ml-2 rounded-full bg-accent-gold px-2 py-0.5 text-xs font-bold" style={{ color: '#1e1226' }}>Tu alquiler</span>}
                            </span>
                            {!isPro && <button className="btn btn-sm btn-outline" onClick={() => release(r, room.roomName, day.date)}>Liberar</button>}
                            {isPro && !r.mine && (() => {
                              const text = `Hola ${firstName(r.professionalName)}, soy ${user?.name || 'un colega'}. Necesito ${room.roomName} el ${day.date.split('-').reverse().join('/')} de ${r.start} a ${r.end}. ¿Podemos coordinar?`;
                              const phone = (r.professionalPhone || '').replace(/\D/g, '');
                              return (
                                <span className="flex gap-2">
                                  {phone && <a className="btn btn-sm btn-outline" target="_blank" rel="noreferrer" href={`https://wa.me/${phone}?text=${encodeURIComponent(text)}`}><MessageCircle className="mr-1 h-3.5 w-3.5" aria-hidden="true" />WhatsApp</a>}
                                  {r.professionalEmail && <a className="btn btn-sm btn-outline" href={`mailto:${r.professionalEmail}?subject=${encodeURIComponent(`${room.roomName} el ${day.date.split('-').reverse().join('/')}`)}&body=${encodeURIComponent(text)}`}><Mail className="mr-1 h-3.5 w-3.5" aria-hidden="true" />Email</a>}
                                </span>
                              );
                            })()}
                          </li>
                        ))}
                        {room.free.length > 0 && <li className="px-3 py-1 text-green-700">Libre: {room.free.map(f => `${f.start}–${f.end}`).join(' · ')}</li>}
                        {room.rentals.length === 0 && room.free.length === 0 && <li className="px-3 py-1 text-gray-500">Cerrado</li>}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </>)}
    </div>
  );
};

export default RoomOccupancy;
